// eslint-disable-next-line no-restricted-imports
import { Link, LinkProps } from "@tanstack/react-router";
import { forwardRef } from "react";
import { useCurrentLanguage } from "@/hooks/useI18nNavigation";

// Get all route paths from FileRoutesByPath
type FileRoutesByPath = import("@tanstack/react-router").FileRoutesByPath;

// Extract all full paths
type AllRoutePaths = keyof FileRoutesByPath;

// Remove the /$lang prefix from routes to get the base path
type RemoveLangPrefix<T extends string> = T extends `/$lang${infer Rest}`
  ? Rest extends ""
    ? "/"
    : Rest
  : T;

// Get all routes that start with /$lang and remove the prefix
type LangRoutes = {
  [K in AllRoutePaths]: K extends `/$lang${string}` ? RemoveLangPrefix<K> : never;
}[AllRoutePaths];

interface LangLinkProps extends Omit<LinkProps, "to"> {
  // Accept routes without the /$lang prefix (they'll be added automatically)
  to: LangRoutes | (string & {}); // Allow string for external URLs

  // Add common HTML attributes that might be needed
  id?: string;
  style?: React.CSSProperties;
  "aria-label"?: string;
  className?: string;
  onClick?: React.MouseEventHandler<"a">;
}

/**
 * Language-aware Link component that automatically includes the current language prefix
 */
export const LangLink = forwardRef<HTMLAnchorElement, LangLinkProps>(
  ({ to, params, onClick, ...props }, ref) => {
    const currentLang = useCurrentLanguage();

    // Check if it's an external URL (starts with http/https)
    const isExternalUrl = to.startsWith("http://") || to.startsWith("https://");

    // If external URL, use as-is; otherwise add language prefix
    const toWithLang = isExternalUrl
      ? to
      : to.startsWith("/$lang")
        ? to
        : `/$lang${to.startsWith("/") ? to : `/${to}`}`;

    return (
      // eslint-disable-next-line react/forbid-elements
      <Link
        {...props}
        ref={ref}
        to={toWithLang as any}
        params={
          params !== undefined && params !== true
            ? isExternalUrl
              ? params
              : {
                  lang: currentLang,
                  ...params,
                }
            : undefined
        }
        onClick={onClick as any}
      />
    );
  },
);

LangLink.displayName = "LangLink";
